import React from 'react';
import { Loader2, Send, Trash2, UserPlus } from 'lucide-react';
import { Company, InviteUserDto, User } from '../types';
import { getCompanyName, getRoleIcon, getRoleBadge } from '../utils';
import { useDeleteUser, useSendUserInvite, useUsers } from '@/core/api/queries/users';
import { useUIState } from '@/core/UIStateProvider';
import { useCompanies } from '@/core/api/queries';

interface UsersTabProps {
  setShowAddUser: (show: boolean) => void;
}

export const UsersTab: React.FC<UsersTabProps> = ({ setShowAddUser }) => {
  const companies = useCompanies();

  const companiesData = (companies.data as Company[]) || [];
  const users = useUsers();

  const usersData = (users.data as User[]) || [];

  const sendUserInvite = useSendUserInvite();
  const deleteUser = useDeleteUser();

  const { addNotification } = useUIState();
  const handleSendInvite = async (userId: string, userRole: string): Promise<void> => {
    try {
      await sendUserInvite.mutateAsync({ userId, data: { role: userRole as InviteUserDto['role'] } });
      addNotification('success', 'Invitation sent successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send invite';
      addNotification('error', errorMessage);
    }
  };

  const handleDeleteUser = async (userId: string): Promise<void> => {
    try {
      if (confirm('Delete this user?')) {
        await deleteUser.mutateAsync(userId);
        addNotification('success', 'User deleted successfully!');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete user';
      addNotification('error', errorMessage);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Users</h2>
        <button
          onClick={() => setShowAddUser(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <UserPlus size={18} />
          <span>Add User</span>
        </button>
      </div>

      {users.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Loading users...</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">User</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Role</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Company</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Joined</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {usersData.map((user) => (
                <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        {getRoleIcon(user.role)}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadge(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-gray-600">{getCompanyName(user.companyId, companiesData)}</span>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-sm text-gray-500">{new Date(user.createdAt).toLocaleDateString()}</span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleSendInvite(user.id, user.role.toLocaleLowerCase())}
                        disabled={sendUserInvite.isPending}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Send invite"
                      >
                        <Send size={16} />
                      </button>

                      <div className="flex justify-end">
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={user.role === 'super_admin'}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:text-gray-400"
                          title="Delete user"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
